/**
 * Admin → Activity Stream — system-wide notification + email feed.
 *
 * Every paired in-app + email send shows here. The notification table
 * is our audit log; on staging emails go to `MAIL_DISABLED_SUPPRESSED`
 * log lines, but the in-app row still lands here, so the stream gives
 * admins ground truth on "what got sent to whom" without needing
 * kubectl access.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { formatRelative } from '../lib/relativeTime'
import { ErrorCard } from '../components/ErrorCard'
import {
  Badge,
  Button,
  Caption1,
  Dropdown,
  Input,
  Option,
  Spinner,
  Subtitle2,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Mail24Regular, Search20Regular } from '@fluentui/react-icons'
import api from '../lib/api'

interface ActivityItem {
  id: number
  type: 'application' | 'demand' | 'focus_area' | 'system' | 'comment'
  message: string
  related_id: number | null
  read: boolean
  created_at: string | null
  recipient_id: number
  recipient_name: string
  recipient_email: string
  recipient_role: string
  recipient_is_test: boolean
}

const TYPE_OPTIONS: { value: '' | ActivityItem['type']; label: string }[] = [
  { value: '', label: 'All events' },
  { value: 'application', label: 'Application' },
  { value: 'demand', label: 'Demand' },
  { value: 'focus_area', label: 'Focus area' },
  { value: 'comment', label: 'Comment' },
  { value: 'system', label: 'System' },
]

const TYPE_COLOR: Record<ActivityItem['type'], 'brand' | 'success' | 'warning' | 'subtle' | 'severe'> = {
  application: 'brand',
  demand: 'success',
  focus_area: 'warning',
  comment: 'subtle',
  system: 'severe',
}

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  helper: {
    color: tokens.colorNeutralForeground3,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  searchWrap: { flex: '1 1 240px', minWidth: '200px' },
  tableWrap: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  rowMeta: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  rowLink: {
    cursor: 'pointer',
    ':hover': { background: '#F5F5F5' },
  },
  unreadDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: tokens.colorPaletteBlueForeground2,
    display: 'inline-block',
    marginRight: '6px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    color: tokens.colorNeutralForeground3,
  },
})

export function AdminActivityStream() {
  const styles = useStyles()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [type, setType] = useState<'' | ActivityItem['type']>('')
  const [includeTest, setIncludeTest] = useState(true)
  const [page, setPage] = useState(0)
  const pageSize = 200

  const { data, isLoading, isError, refetch } = useQuery<ActivityItem[]>({
    queryKey: ['admin', 'activity', { type, includeTest, search: q.trim(), page }],
    queryFn: async ({ signal }) => {
      const params: Record<string, string | boolean | number> = {
        include_test: includeTest,
        limit: pageSize + 1,
        offset: page * pageSize,
        search: q.trim(),
      }
      if (type) params.type = type
      const res = await api.get('/admin/activity', { params, signal })
      return res.data
    },
    refetchInterval: 30_000,
  })

  const rows = (data ?? []).slice(0, pageSize)
  const hasNext = (data?.length ?? 0) > pageSize

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <Title2 block>Activity stream</Title2>
        </div>
        <Subtitle2>{rows.length} shown</Subtitle2>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Input
            value={q}
            onChange={(_, d) => {
              setQ(d.value)
              setPage(0)
            }}
            contentBefore={<Search20Regular />}
            placeholder="Search message / recipient"
          />
        </div>
        <Dropdown
          value={TYPE_OPTIONS.find((o) => o.value === type)?.label || 'All events'}
          selectedOptions={[type]}
          onOptionSelect={(_, d) => {
            setType((d.optionValue as '' | ActivityItem['type']) ?? '')
            setPage(0)
          }}
        >
          {TYPE_OPTIONS.map((o) => (
            <Option key={o.value || 'all'} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Dropdown>
        <Switch
          checked={includeTest}
          onChange={(_, d) => {
            setIncludeTest(d.checked)
            setPage(0)
          }}
          label="Include test accounts"
        />
      </div>

      {isLoading ? (
        <div className={styles.empty}>
          <Spinner size="small" />
        </div>
      ) : isError ? (
        <div className={styles.empty} data-testid="activity-error">
          <ErrorCard
            title="Could not load activity"
            message="Please try again."
            retry={() => void refetch()}
          />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>No activity matches the current filters.</div>
      ) : (
        <div className={styles.tableWrap}>
          <Table aria-label="System activity stream" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '14%' }}>When</TableHeaderCell>
                <TableHeaderCell style={{ width: '10%' }}>Type</TableHeaderCell>
                <TableHeaderCell style={{ width: '24%' }}>Recipient</TableHeaderCell>
                <TableHeaderCell style={{ width: '42%' }}>Message</TableHeaderCell>
                <TableHeaderCell style={{ width: '10%' }}>Channel</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((n) => {
                const clickable = n.related_id != null
                return (
                  <TableRow
                    key={n.id}
                    className={clickable ? styles.rowLink : undefined}
                    onClick={clickable ? () => navigate(`/demand/${n.related_id}`) : undefined}
                  >
                    <TableCell>
                      <Caption1>{formatRelative(n.created_at, '—')}</Caption1>
                    </TableCell>
                    <TableCell>
                      <Badge appearance="outline" color={TYPE_COLOR[n.type] || 'subtle'}>
                        {n.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        {!n.read && <span className={styles.unreadDot} aria-label="unread" />}
                        {n.recipient_name}
                        {n.recipient_is_test && (
                          <Badge
                            appearance="tint"
                            color="warning"
                            size="small"
                            style={{ marginLeft: 6 }}
                          >
                            TEST
                          </Badge>
                        )}
                      </div>
                      <div className={styles.rowMeta}>
                        {n.recipient_email} · {n.recipient_role}
                      </div>
                    </TableCell>
                    <TableCell>
                      {clickable ? (
                        <Link to={`/demand/${n.related_id}`} onClick={(e) => e.stopPropagation()}>
                          {n.message}
                        </Link>
                      ) : n.message}
                    </TableCell>
                    <TableCell>
                      <Badge
                        appearance="tint"
                        color="informative"
                        icon={<Mail24Regular />}
                        size="small"
                      >
                        in-app + email
                      </Badge>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <div className={styles.toolbar} aria-label="Activity pages">
        <Button disabled={page === 0 || isLoading} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <Caption1>Page {page + 1}</Caption1>
        <Button disabled={!hasNext || isLoading || isError} onClick={() => setPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  )
}
